import {isFeatureEnabled} from 'feature-flags';
import {
  Alert,
  Button,
  Card,
  Dropdown,
  DropdownItem,
  type TabProps,
  Tabs,
} from 'platform/components';
import {Box, Grid, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useGetVehicleQuery} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import {ConfigurationCustomerView} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {usePricePermissions} from '@dms/shared';
import {PriceBox, VehicleWidget} from '@dms/teas';

import {buildArray, Nullish} from 'shared/web';

import {BusinessCaseSaleVehicleComments} from '../BusinessCaseSaleVehicleComments';
import {AlternativeDiscountForm} from './AlternativeDiscountForm';
import {AlternativeOfferTermsForm} from './AlternativeOfferTermsForm';
import {AlternativePriceBox} from './AlternativePriceBox';
import {AlternativeUpsellCard} from './AlternativeUpsellCard';
import {getAlternativeFlags} from './getAlternativeFlags';
import {type OfferAlternativeWithRights} from './getAlternativeRights';
import {getUnavailableMessage} from './getUnavailableMessage';
import {type OfferAlternative, type OfferAlternativesCustomerChoice} from './OfferAlternative';
import {useOfferAlternativeActions} from './useOfferAlternativeActions';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

const TAB_IDS = {SELLING: 'selling', SPECIFICATION: 'specification', COMMENTS: 'comments'};

interface AlternativeTermsPanelProps {
  businessCaseId: string;
  alternative: OfferAlternativeWithRights;
  customerChoice: OfferAlternativesCustomerChoice | Nullish;
  /** The alternative the customer currently chooses; applying another one asks first. */
  chosenAlternative: OfferAlternative | Nullish;
}

/**
 * Selling terms of a non-applied alternative, in the same layout as the deal vehicle: vehicle,
 * price summary, discount, upsell and the customer-facing terms.
 */
export function AlternativeTermsPanel(props: AlternativeTermsPanelProps) {
  const summary = props.alternative.summary;
  const [activeTabId, setActiveTabId] = useState(TAB_IDS.SELLING);
  const actions = useOfferAlternativeActions(props.businessCaseId);
  const permissions = usePricePermissions({
    businessCaseRecordId: props.businessCaseId,
    vehicleRecordId: props.alternative.vehicleId,
  });
  const {data: stockVehicle} = useGetVehicleQuery(
    {vehicleId: props.alternative.vehicleId ?? ''},
    {skip: isNil(props.alternative.vehicleId)}
  );
  const unavailableMessage = getUnavailableMessage(props.alternative.unavailableReason);
  const canRefresh =
    props.alternative.canEdit && props.alternative.unavailableReason === 'SOURCE_CHANGED';
  const hasMenu =
    props.alternative.canApply ||
    props.alternative.canChangeSource ||
    canRefresh ||
    props.alternative.canWithdraw;

  // A configuration has no vehicle yet, so only a stock alternative has vehicle comments.
  const extraTabs = buildArray<TabProps>()
    .whenNotNil(
      props.alternative.kind === 'CONFIGURATION' ? props.alternative.configurationId : null,
      (configurationId) => ({
        id: TAB_IDS.SPECIFICATION,
        title: t('entity.offerAlternative.tabs.specification', 'Specification'),
        content: (
          <Box paddingTop={2}>
            <ConfigurationCustomerView configurationId={configurationId} />
          </Box>
        ),
      })
    )
    .whenNotNil(
      props.alternative.kind === 'STOCK' &&
        isFeatureEnabled(featureFlags.SALES_SALE_VEHICLE_COMMENTS)
        ? props.alternative.vehicleId
        : null,
      (vehicleId) => ({
        id: TAB_IDS.COMMENTS,
        title: i18n.t('entity.vehicle.labels.vehicleComments'),
        content: <BusinessCaseSaleVehicleComments vehicleId={vehicleId} />,
      })
    );

  // Same price-read gates as the deal vehicle: its summary needs any price field, its discount
  // and upsell (Selling tab) the sale-vehicle selling price. The customer terms carry no price.
  const canReadPrices =
    permissions.canViewAnyOfSalePriceFields || permissions.canViewAnyOfPurchasePriceFields;
  const sellingContent = (
    <VStack spacing={4}>
      <Show when={permissions.canReadBusinessCaseSaleVehicleSalePrice}>
        <AlternativeDiscountForm
          businessCaseId={props.businessCaseId}
          alternative={props.alternative}
        />
        <AlternativeUpsellCard
          businessCaseId={props.businessCaseId}
          alternative={props.alternative}
        />
      </Show>
      <AlternativeOfferTermsForm
        businessCaseId={props.businessCaseId}
        alternative={props.alternative}
      />
    </VStack>
  );

  return (
    <Card data-testid="offerAlternativePanel">
      <VStack spacing={4}>
        <Show when={unavailableMessage}>
          {(message) => (
            <Alert
              variant="warning"
              layout="detailed"
              message={message}
              actions={
                canRefresh
                  ? [
                      {
                        variant: 'primary' as const,
                        title: t(
                          'entity.offerAlternative.actions.refreshFromSource',
                          'Refresh from source'
                        ),
                        onClick: () => actions.refreshFromSource(props.alternative),
                      },
                    ]
                  : undefined
              }
            />
          )}
        </Show>
        <VehicleWidget
          data-testid="offerAlternativeVehicle"
          vehicle={
            stockVehicle ?? {
              title: props.alternative.title,
              vin: props.alternative.vin ?? undefined,
            }
          }
          shouldShowDetailButton={props.alternative.kind === 'STOCK'}
          // The widget's price box reads a deal vehicle; the summary below shows the alternative's.
          shouldShowPrice={false}
          flags={getAlternativeFlags(props.alternative, props.customerChoice)}
          extraControls={
            <Show when={hasMenu}>
              <Dropdown
                dropdownControl={
                  <Button
                    variant="outlined"
                    leftIcon="navigation/more_vert"
                    title={i18n.t('page.businessCase.labels.actions')}
                    data-testid="offerAlternativeActions"
                  />
                }
              >
                <Show when={props.alternative.canApply}>
                  <DropdownItem
                    label={t('entity.offerAlternative.actions.apply', 'Use for checkout')}
                    leftIcon="action/check_circle"
                    onClick={() =>
                      actions.applyOverChoice(props.alternative, props.chosenAlternative)
                    }
                  />
                </Show>
                <Show when={props.alternative.canChangeSource}>
                  <DropdownItem
                    label={t('entity.offerAlternative.actions.changeSource', 'Change vehicle')}
                    leftIcon="navigation/refresh"
                    onClick={() => actions.changeSource(props.alternative)}
                  />
                </Show>
                <Show when={canRefresh}>
                  <DropdownItem
                    label={t(
                      'entity.offerAlternative.actions.refreshFromSource',
                      'Refresh from source'
                    )}
                    leftIcon="action/autorenew"
                    onClick={() => actions.refreshFromSource(props.alternative)}
                  />
                </Show>
                <Show when={props.alternative.canWithdraw}>
                  <DropdownItem
                    severity="danger"
                    label={t('entity.offerAlternative.actions.withdraw', 'Withdraw alternative')}
                    leftIcon="action/delete"
                    onClick={() => actions.withdraw(props.alternative)}
                  />
                </Show>
              </Dropdown>
            </Show>
          }
        />
        <Show when={canReadPrices}>
          <Card
            variant="inlineGrey"
            title={i18n.t('page.businessCase.labels.salVehiclePriceSummary')}
            isExpandable
          >
            {/* Prices only: an alternative carries no costs, so no earnings or margins either. */}
            <Grid columns={[1, 2, 4]} data-testid="offerAlternativeSummary">
              <Show when={permissions.canReadVehicleSellingPrice}>
                <AlternativePriceBox
                  align="left"
                  title={i18n.t('entity.businessCase.labels.vehicleSellingPrice')}
                  price={summary.vehicleSellingPrice}
                />
              </Show>
              <AlternativePriceBox
                align="left"
                title={i18n.t('entity.businessCase.labels.upsell')}
                price={summary.upsell}
              />
              {/* Null is also "no discount", so a withheld discount keeps the empty price. */}
              <PriceBox
                align="left"
                title={i18n.t('general.labels.discount')}
                // Signed like the deal vehicle's discount box.
                price={
                  summary.discount
                    ? {...summary.discount.withVat, amount: `-${summary.discount.withVat.amount}`}
                    : undefined
                }
                priceSub={summary.discount?.withoutVat}
              />
              <Show when={permissions.canReadBusinessCaseSaleTotalPrice}>
                <AlternativePriceBox
                  align="left"
                  title={i18n.t('entity.businessCase.labels.totalPrice')}
                  price={summary.totalPrice}
                />
              </Show>
            </Grid>
          </Card>
        </Show>
        {extraTabs.length > 0 ? (
          <Tabs
            variant="condensed"
            activeTabId={activeTabId}
            onChange={setActiveTabId}
            tabs={[
              {
                id: TAB_IDS.SELLING,
                title: i18n.t('page.businessCase.labels.typeSelling'),
                content: sellingContent,
              },
              ...extraTabs,
            ]}
          />
        ) : (
          sellingContent
        )}
      </VStack>
    </Card>
  );
}
